import { useEffect, useState } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import Container from '../../components/Container';
import Header from '../../components/Header';
import Button from '../../components/Button';
import FlexContainer from '../../components/FlexContainer';
import { iconForItem } from '../../img/sales';

interface SaleItem {
    _id: string;
    name: string;
    price: number;
    active: boolean;
    sortOrder: number;
}

const blank = { name: '', price: 0, sortOrder: 0, active: true };

const SalesItemsPage = () => {
    const history = useHistory();
    const [items, setItems] = useState<SaleItem[] | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ ...blank });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = async () => {
        try {
            const { data } = await axios.get<SaleItem[]>('/api/sales-items');
            setItems(data);
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Failed to load.');
        }
    };

    useEffect(() => {
        load();
    }, []);

    const startEdit = (i: SaleItem) => {
        setEditingId(i._id);
        setForm({
            name: i.name,
            price: i.price,
            sortOrder: i.sortOrder,
            active: i.active,
        });
        setShowForm(true);
    };
    const startAdd = () => {
        setEditingId(null);
        setForm({ ...blank, sortOrder: items ? items.length + 1 : 0 });
        setShowForm(true);
    };
    const cancelEdit = () => {
        setEditingId(null);
        setForm({ ...blank });
        setShowForm(false);
    };
    const save = async () => {
        setBusy(true);
        setError(null);
        try {
            if (editingId) {
                await axios.patch(`/api/sales-items/${editingId}`, form);
            } else {
                await axios.post('/api/sales-items', form);
            }
            cancelEdit();
            await load();
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Save failed.');
        } finally {
            setBusy(false);
        }
    };
    const remove = async (id: string) => {
        if (!window.confirm('Delete this item?')) return;
        setBusy(true);
        try {
            await axios.delete(`/api/sales-items/${id}`);
            if (editingId === id) cancelEdit();
            await load();
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Delete failed.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Container containerName={'landing'}>
            <Header
                title={'Sale Items'}
                subtitle={'Tap an item to edit'}
                bottomBorder={true}
                padding={true}
            />
            <FlexContainer>
                {error && (
                    <h3 className="transaction-container__text">
                        <span className="red-span">{error}</span>
                    </h3>
                )}

                {items && (
                    <div className="button-container">
                        {items.map((i) => {
                            const icon = iconForItem(i.name);
                            return (
                                <label className="label-block" key={i._id}>
                                    <button
                                        className="label-block__button"
                                        onClick={() => startEdit(i)}
                                        style={{ opacity: i.active ? 1 : 0.5 }}
                                    >
                                        {icon ? (
                                            <img
                                                className="button-icon"
                                                src={icon}
                                                alt={i.name}
                                                style={{
                                                    maxWidth: '100%',
                                                    height: '7.5rem',
                                                    width: 'auto',
                                                    objectFit: 'contain',
                                                }}
                                            />
                                        ) : (
                                            <div
                                                className="button-icon"
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '3rem',
                                                    fontWeight: 700,
                                                    color: '#9B3737',
                                                }}
                                            >
                                                ${i.price}
                                            </div>
                                        )}
                                    </button>
                                    {i.name}
                                    {!i.active && ' (off)'}
                                </label>
                            );
                        })}
                        <label className="label-block">
                            <button
                                className="label-block__button"
                                onClick={startAdd}
                            >
                                <div
                                    className="button-icon"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '5rem',
                                        fontWeight: 700,
                                        color: '#379B37',
                                    }}
                                >
                                    +
                                </div>
                            </button>
                            Add Item
                        </label>
                    </div>
                )}

                {showForm && (
                    <>
                        <h3 className="transaction-container__text">
                            <span className="red-span">
                                {editingId ? `Edit: ${form.name}` : 'Add Item'}
                            </span>
                        </h3>

                        <span className="form-span">Name</span>
                        <input
                            className="form-input"
                            type="text"
                            value={form.name}
                            onChange={(e) =>
                                setForm({ ...form, name: e.target.value })
                            }
                        />

                        <span className="form-span">Price</span>
                        <input
                            className="form-input"
                            type="number"
                            step="0.01"
                            min="0"
                            value={form.price}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    price: Number(e.target.value),
                                })
                            }
                        />

                        <span className="form-span">Sort Order</span>
                        <input
                            className="form-input"
                            type="number"
                            value={form.sortOrder}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    sortOrder: Number(e.target.value),
                                })
                            }
                        />

                        <span className="form-span">
                            <input
                                type="checkbox"
                                checked={form.active}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        active: e.target.checked,
                                    })
                                }
                            />{' '}
                            Active
                        </span>

                        <div>
                            <Button
                                color={'green'}
                                margin={true}
                                onClick={save}
                            >
                                {busy ? 'Saving…' : editingId ? 'Save' : 'Add'}
                            </Button>
                            <Button
                                color={'red'}
                                margin={true}
                                onClick={cancelEdit}
                            >
                                Cancel
                            </Button>
                            {editingId && (
                                <Button
                                    color={'red'}
                                    margin={true}
                                    onClick={() => remove(editingId)}
                                >
                                    Delete
                                </Button>
                            )}
                        </div>
                    </>
                )}

                <Button
                    color={'red'}
                    margin={true}
                    onClick={() => history.push('/dashboard')}
                >
                    Back to Dashboard
                </Button>
            </FlexContainer>
        </Container>
    );
};

export default SalesItemsPage;
